import _ from 'lodash';
import React from 'react';
import { getIntl } from 'umi';

import Badge, { BadgeGroup } from '@/components/Badge';
import HighlighterCustom from '@/components/HighlighterCustom';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import UserRole from '@/utils/domain/constants/enumerator/UserRole';
import {
  formatRoleName,
  normalizeRoleToMember,
  ROLE_NAME_TRANSLATION_PREFIX,
} from '@/utils/domain/constants/roles';

const USER_ROLE_TO_NAME_KEY = Object.freeze({
  UserManager: 'UserAdmin',
});

export const getUserRoleFilterOptions = () =>
  Object.entries(UserRole)
    .map(([key, legacyValue]) => ({
      value: legacyValue,
      label: `${ROLE_NAME_TRANSLATION_PREFIX}.${_.camelCase(USER_ROLE_TO_NAME_KEY[key] || key)}`,
      locale: true,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const resolveUserRoles = (record) => {
  if (!record) return [];

  const { roles, role } = record;

  if (Array.isArray(roles) && roles.length > 0) {
    return roles.map(normalizeRoleToMember).filter(Boolean);
  }

  const legacyMember = normalizeRoleToMember(role);
  return legacyMember ? [legacyMember] : [];
};

export const getUserRoleLabels = (record, formatMessage) =>
  resolveUserRoles(record).map((member) => formatRoleName(member, formatMessage));

export const renderUserRolesBadges = (record, formatMessage, searchCriteria) => {
  const labels = getUserRoleLabels(record, formatMessage);
  if (!labels.length) return DEFAULT_EMPTY_VALUE;

  return (
    <BadgeGroup>
      {labels.map((label) => (
        <Badge
          key={label}
          status="default"
          type="process"
          text={<HighlighterCustom textToHighlight={label} searchTerm={searchCriteria} />}
        />
      ))}
    </BadgeGroup>
  );
};

export const normalizeRolesPayload = (roles) => {
  if (roles == null) return [];

  const values = Array.isArray(roles) ? roles : [roles];
  return values.map(normalizeRoleToMember).filter(Boolean);
};

export const mapRoleAllowedSelectOptions = (data) => {
  if (!Array.isArray(data) || !data.length) return data;

  const formatMessage = getIntl()?.formatMessage;
  if (!formatMessage) return data;

  return data.map((item) => {
    const member = normalizeRoleToMember(item.label) ?? normalizeRoleToMember(item.value);
    if (!member) return item;

    return {
      ...item,
      label: formatRoleName(member, formatMessage),
      value: member,
    };
  });
};
